import { httpClient, objectToFormData } from 'api';
import { Application } from 'types/Application';
import { Post } from 'types/Dashboard';
import { ProgramMembership, ProgramMembershipStats } from 'types/Membership';
import { Paginated } from 'types/Paginated';
import { Pair, PairExtended } from 'types/Pair';
import {
	Program,
	ProgramLandingPageModuleDefine,
	ProgramLandingPageModule,
	UpdateProgramPairsParametersPayload,
	UpdateProgramRecruitmentParametersPayload,
	UpdateProgramSettingsParametersPayload,
	UpdateProgramBaseConfigParametersPayload,
	ProgramLandingPageModulesAddPayload,
	ProgramLandingPageModulesEditPayload,
	ProgramLandingPageModulesSequencePayload,
	ProgramLandingPageModulesImagePayload,
	UpdateProgramContentConfigParametersPayload,
} from 'types/Program';
import { User } from 'types/User';
import { ApplicationChangelogEntry } from '../types/Changelog';

export const programsApi = {
	getNextPrograms: (programId: number) =>
		httpClient.get<Array<Pick<Program, 'id' | 'name' | 'displayName'>>>(
			`programs/admin/${programId}/next-programs`
		),
	createForOrganization: (organizationName: string, formData: FormData) =>
		httpClient.post<Program>(`programs/organization/${organizationName}`, formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}),
	editProgram: ({
		id,
		displayName,
		// name,
		language,
		authRequired = false,
		programType,
	}: {
		id: number;
		displayName: string;
		// name: string;
		language: string;
		authRequired: boolean;
		programType: string;
	}) =>
		httpClient.put<Program>(`programs/${id}`, {
			displayName,
			// name,
			language,
			authRequired,
			programType,
		}),
	copyProgram: ({
		organizationName,
		sourceProgramId,
		displayName,
		name,
		copyOptions,
	}: {
		organizationName: string;
		sourceProgramId: string;
		displayName: string;
		name: string;
		copyOptions: {
			landingPageContent: boolean;
			contractSettings: boolean;
			recruitmentForms: boolean;
			knowledgeBaseMaterials: boolean;
		};
	}) =>
		httpClient.post<any>(`programs/organization/${organizationName}/copy`, {
			sourceProgramId,
			displayName,
			name,
			copyOptions,
		}),
	// getProgramWithMembershipInfo: (organizationName: string, programName: string) =>
	// 	httpClient.get<ProgramInfo>(`programs/${organizationName}/${programName}/info`),
	getProgramContact: (programId: number) => httpClient.get<string>(`programs/${programId}/contact`),
	getProgram: (organizationName: string, programName: string) =>
		httpClient.get<Program>(`programs/${organizationName}/${programName}`),
	getProgramAdminLabelFiles: ({ programId }: { programId: number }) =>
		httpClient.get<Array<{ id: number; name: string }>>(`programs/admin/${programId}/label-files`),
	getProgramAdmin: ({ organizationName, programName }: { organizationName: string; programName: string }) =>
		httpClient.get<Program>(`programs/admin/${organizationName}/${programName}`),
	// getProgramByName: ({ programName }: { programName: string }) =>
	// 	axiosInstance.get<Program>(`programs/${programName}`),
	getProgramAdminMemberships: ({ id, page, filter }: { id: number; page: number; filter: any }) =>
		httpClient.get<
			Paginated<
				Omit<ProgramMembership, 'user' | 'stats' | 'applications'> & {
					user: User;
					stats: ProgramMembershipStats;
					applications: Application[];
					canDelete: boolean;
				}
			>
		>(`programs/admin/${id}/memberships`, { page, filter }),

	downloadProgramAdminMembershipsXls: ({ programId }: { programId: number }) =>
		httpClient.get<Blob>(`programs/admin/${programId}/memberships/xls`, undefined, { responseType: 'blob' }),

	getProgramMembership: ({ programId, membershipId }: { programId: number; membershipId: number }) =>
		httpClient.get<any>(`programs/admin/${programId}/membership/${membershipId}`),
	getProgramPairsPaginated: ({ id, page, filter, sort }: { id: number; page: number; filter?: any; sort?: any }) =>
		httpClient.get<Paginated<PairExtended>>(`programs/admin/${id}/pairs`, { page, filter, sort }),
	// getProgramPairs: ({ id, acceptedOnly }: { id: number; acceptedOnly?: boolean }) =>
	// 	httpClient.get<PairExtended[]>(`programs/admin/${id}/pairs`, { acceptedOnly }),
	manageMyProgramMembership: ({
		id,
		membershipId,
		role,
		pairs,
	}: {
		id: number;
		membershipId: number;
		role: string;
		pairs: number[];
	}) =>
		httpClient.put<any>(`programs/admin/${id}/membership/${membershipId}`, {
			role,
			pairs,
		}),
	removeMyProgramMembership: ({ id, membershipId }: { id: number; membershipId: number }) =>
		httpClient.delete<any>(`programs/admin/${id}/membership/${membershipId}`),
	removeAllWithoutApplicationMutate: ({ programId }: { programId: number }) =>
		httpClient.delete<any>(`programs/admin/${programId}/memberships/without-application`),
	removePersonalData: ({
		programId,
		membershipId,
		quickDelete,
	}: {
		programId: number;
		membershipId: number;
		quickDelete?: boolean;
	}) =>
		httpClient.put<any>(`programs/admin/${programId}/memberships/${membershipId}/personal-data`, {
			quickDelete,
		}),
	restoreProgramApplication: ({ programId, applicationId }: { programId: number; applicationId: number }) =>
		httpClient.post<void>(`/programs/admin/${programId}/applications/${applicationId}/restore`),
	getStatisticsMain: ({ id }: { id: number }) =>
		httpClient.get<any>(`programs/admin/${id}/statistics/main`, undefined, { timeout: 20000 }),
	getStatisticsQuestionarires: ({ id }: { id: number }) =>
		httpClient.get<any>(`programs/admin/${id}/statistics/questionarires`, undefined, { timeout: 20000 }),
	realoadStatistics: ({ id }: { id: number }) => httpClient.post<any>(`programs/admin/${id}/statistics/reload`),
	getOrganizationPrograms: ({ organizationName }: { organizationName: string }) =>
		httpClient.get<any>(`programs/admin/organization/${organizationName}/programs`),
	getProgramAdminApplicationsAll: ({
		programId,
		applicationRole,
		acceptedOnly = false,
	}: {
		programId: number;
		applicationRole: string;
		acceptedOnly?: boolean;
	}) =>
		httpClient.get<Application[]>(`programs/admin/${programId}/applications/${applicationRole}/all`, {
			acceptedOnly,
		}),
	createProgramAdminLandingPageFromTemplate: (programId: number) =>
		httpClient.post<ProgramLandingPageModule[]>(`programs/admin/${programId}/landing-page-modules/from-template`),
	getProgramAdminApplicationsPaginated: ({
		programId,
		page = 1,
		filter = {},
		sort = {},
	}: {
		programId: number;
		page?: number;
		filter?: any;
		sort?: any;
	}) =>
		httpClient.get<Paginated<Application, { maxPairLimit: number }>>(
			`programs/admin/${programId}/applications/paginated`,
			{
				page,
				filter,
				sort,
			}
		),
	putProgramAdminParameters: (programId: number, data: any) =>
		httpClient.put<Program>(`programs/admin/${programId}`, data),
	putProgramAdminBaseConfigParameters: (programId: number, data: UpdateProgramBaseConfigParametersPayload) =>
		httpClient.put<Program>(
			`programs/admin/${programId}/base`,
			objectToFormData(data, undefined, { skipUndefined: true })
		),
	putProgramAdminContentConfigParameters: (programId: number, data: UpdateProgramContentConfigParametersPayload) =>
		httpClient.put<Program>(
			`programs/admin/${programId}/content`,
			objectToFormData(data, undefined, { skipUndefined: true })
		),
	putProgramAdminRecruitmentParameters: (programId: number, data: UpdateProgramRecruitmentParametersPayload) =>
		httpClient.put<Program>(`programs/admin/${programId}/recruitment`, data),
	putProgramAdminSettingsParameters: (programId: number, data: UpdateProgramSettingsParametersPayload) =>
		httpClient.put<Program>(`programs/admin/${programId}/settings`, data),
	putProgramAdminPairsParameters: (programId: number, data: UpdateProgramPairsParametersPayload) =>
		httpClient.put<Program>(`programs/admin/${programId}/pairs`, data),
	editProgramAdminContact: (programId: number, contact: string) =>
		httpClient.put<Program>(`programs/admin/${programId}/contact`, { contact }),
	getProgramAdminApplication: ({ programId, applicationId }: { programId: number; applicationId: number }) =>
		httpClient.get<Application>(`programs/admin/${programId}/application/${applicationId}`),
	getProgramApplicationBusinessCardAdmin: ({
		programId,
		applicationId,
	}: {
		programId: number;
		applicationId: number;
	}) => httpClient.get<Application>(`programs/admin/${programId}/application/${applicationId}/business-card`),
	getProgramAdminApplicationPairing: ({ programId, applicationId }: { programId: number; applicationId: number }) =>
		httpClient.get<Pair[]>(`programs/admin/${programId}/application/${applicationId}/pairing`),
	getProgramAdminPair: ({ programId, pairId }: { programId: number; pairId: number }) =>
		httpClient.get<PairExtended>(`programs/admin/${programId}/pair/${pairId}`),
	getProgramAdminPosts: (programId: number, page: number, perPage: number) =>
		httpClient.get<Post[]>(`programs/admin/${programId}/posts`, { page, perPage }),

	getProgramAdminLandingPageModuleDefines: (programId: number) =>
		httpClient.get<ProgramLandingPageModuleDefine[]>(`programs/admin/${programId}/landing-page-module-defines`),
	getProgramAdminLandingPageModule: (programId: number) =>
		httpClient.get<ProgramLandingPageModule[]>(`programs/admin/${programId}/landing-page-modules`),
	getApplicationLastChange: ({ programId, applicationId }: { programId: number; applicationId: number }) =>
		httpClient.get<ApplicationChangelogEntry | null>(
			`programs/admin/${programId}/application/${applicationId}/changelog/last`
		),

	getApplicationChangelogHistory: ({ programId, applicationId }: { programId: number; applicationId: number }) =>
		httpClient.get<ApplicationChangelogEntry[]>(
			`programs/admin/${programId}/application/${applicationId}/changelog`
		),
	addProgramAdminLandingPageModule: (programId: number, data: ProgramLandingPageModulesAddPayload) =>
		httpClient.post<ProgramLandingPageModule[]>(`programs/admin/${programId}/landing-page-modules`, data),
	uploadProgramAdminLandingPageModuleImage: (programId: number, { file }: ProgramLandingPageModulesImagePayload) => {
		const formData = new FormData();
		formData.append('file', file);
		return httpClient.post<ProgramLandingPageModule[]>(
			`programs/admin/${programId}/landing-page-modules/image`,
			formData
		);
	},
	uploadProgramAdminLandingPageModuleEditorImage: (programId: number, data: FormData) =>
		httpClient.post<string>(`programs/admin/${programId}/landing-page-modules/upload-image`, data),

	setProgramAdminLandingPageModuleSequence: (
		programId: number,
		sequences: ProgramLandingPageModulesSequencePayload
	) => httpClient.put<ProgramLandingPageModule[]>(`programs/admin/${programId}/landing-page-modules`, { sequences }),
	editProgramAdminLandingPageModule: (programId: number, id: number, data: ProgramLandingPageModulesEditPayload) =>
		httpClient.put<ProgramLandingPageModule>(`programs/admin/${programId}/landing-page-modules/${id}`, data),
	deleteProgramAdminLandingPageModule: (programId: number, id: number) =>
		httpClient.delete(`programs/admin/${programId}/landing-page-modules/${id}`),

	// deletePrograAdminPost: ({ programId, postId }: { programId: number; postId: number }) =>
	// 	axiosInstance.delete<boolean>(`programs/admin/${programId}/post/${postId}`),
	// addProgramPost: ({ programId, ...data }: any) => httpClient.post<Post>(`programs/admin/${programId}/post`, data),
	// editProgramPost: ({ programId, postId, ...data }: any) =>
	// 	axiosInstance.put<Post>(`programs/admin/${programId}/post/${postId}`, data),
	// getProgramPost: ({ programId, postId }: any) =>
	// 	axiosInstance.get<Post>(`programs/admin/${programId}/post/${postId}`),
	editProgramApplication: ({
		programId,
		applicationId,
		status,
		reason,
		withNotification = true,
	}: {
		programId: number;
		applicationId: number;
		status: string;
		reason?: string;
		withNotification?: boolean;
	}) =>
		httpClient.put(`programs/admin/${programId}/application/${applicationId}`, {
			status,
			reason,
			withNotification,
		}),
	deleteProgramPair: ({ programId, pairId }: { programId: number; pairId: number }) =>
		httpClient.delete(`programs/admin/${programId}/pair/${pairId}`),
	acceptProgramPair: ({ programId, pairId }: { programId: number; pairId: number }) =>
		httpClient.post(`programs/admin/${programId}/pair/${pairId}`),
	deleteProgramApplications: ({
		programId,
		applicationIds,
		withUser,
		sendMail,
		quickDelete,
	}: {
		programId: number;
		applicationIds: number[];
		withUser: boolean;
		sendMail: boolean;
		quickDelete: boolean;
	}) =>
		httpClient.delete(`programs/admin/${programId}/application/delete`, {
			data: {
				applicationIds,
				withUser,
				sendMail,
				quickDelete,
			},
		}),
	cancelDeleteProgramApplication: ({ programId, applicationId }: { programId: number; applicationId: number }) =>
		httpClient.put(`programs/admin/${programId}/application/delete/${applicationId}`),
	addProgramPair: ({
		programId,
		applicationId,
		pairApplicationId,
	}: {
		programId: number;
		applicationId: number;
		pairApplicationId: number;
	}) => httpClient.put<Pair>(`programs/admin/${programId}/application/${applicationId}/pair`, { pairApplicationId }),
	getAvailablePairsForApplication: ({ programId, applicationId }: { programId: number; applicationId: number }) =>
		httpClient.get<Application[]>(`programs/admin/${programId}/application/${applicationId}/pairs`),

	// contact
	getAvailableMembershipsForTransfer: ({
		programId,
		applicationRole,
		excludeMembershipId,
	}: {
		programId: number;
		applicationRole: 'mentor' | 'mentee';
		excludeMembershipId?: number;
	}) =>
		httpClient.get<ProgramMembership[]>(`programs/admin/${programId}/memberships/available-for-transfer`, {
			applicationRole,
			excludeMembershipId,
		}),

	transferApplicationToMembership: ({
		programId,
		applicationId,
		newMembershipId,
	}: {
		programId: number;
		applicationId: number;
		newMembershipId: number;
	}) =>
		httpClient.post<Application>(`programs/admin/${programId}/application/${applicationId}/transfer`, {
			newMembershipId,
		}),

	getProgramCommunicationMentibotInfo: (programId: number) =>
		httpClient.get<CommunicationMentibotInfoResult>(`programs/admin/${programId}/communication-mentibot-info`),

	getAvailableCoordinatePairsCoordinator: ({ programId }: { programId: number }) =>
		httpClient.get<Pair[]>(`programs/admin/${programId}/available-coordinate-pairs`),

	// coordinator

	getProgramAsCoordinator: ({ organizationName, programName }: { organizationName: string; programName: string }) =>
		httpClient.get<Program>(`programs/coordinator/${organizationName}/${programName}`),
	getPairsPaginatedAsCoordinator: ({
		programId,
		page,
		filter,
		sort,
	}: {
		programId: number;
		page: number;
		filter?: any;
		sort?: any;
	}) => httpClient.get<Paginated<PairExtended>>(`programs/coordinator/${programId}/pairs`, { page, filter, sort }),
	getPairAsCoordinator: ({ programId, pairId }: { programId: number; pairId: number }) =>
		httpClient.get<PairExtended>(`programs/coordinator/${programId}/pair/${pairId}`),
	getProgramApplicationCoordinator: ({ programId, applicationId }: { programId: number; applicationId: number }) =>
		httpClient.get<Application>(`programs/coordinator/${programId}/application/${applicationId}`),
	getProgramApplicationBusinessCardCoordinator: ({
		programId,
		applicationId,
	}: {
		programId: number;
		applicationId: number;
	}) => httpClient.get<Application>(`programs/coordinator/${programId}/application/${applicationId}/business-card`),
	getProgramApplicationPairingCoordinator: ({
		programId,
		applicationId,
	}: {
		programId: number;
		applicationId: number;
	}) => httpClient.get<Pair[]>(`programs/coordinator/${programId}/application/${applicationId}/pairing`),
	getAvailablePairsForApplicationCoordinator: ({
		programId,
		applicationId,
	}: {
		programId: number;
		applicationId: number;
	}) => httpClient.get<Application[]>(`programs/coordinator/${programId}/application/${applicationId}/pairs`),
};

type FinalSurveyNotificationObjectInfo = {
	key: string;
	template: string;
	title: string;
	hours: number;
};

type SessionNotificationObjectInfo = {
	key: string;
	name: string;
	sessionNumber: number;
	sessionNumberName: string;
	title: string;
	forMentorOnly: boolean;
	forMenteeOnly: boolean;
	hours: number;
	template: {
		mail: string;
		post: string;
	};
};

type SessionRemindersNotificationObjectInfo = {
	key: string;
	title: string;
	condition: string;
	template: string;
};

export type CommunicationMentibotInfoResult = {
	sessionNotifications: SessionNotificationObjectInfo[];
	finalSurveyNotifications: FinalSurveyNotificationObjectInfo[];
	sessionRemindersNotifications: SessionRemindersNotificationObjectInfo[];
};
